import { Fragment, ReactNode } from 'react'
import {
  RichTextBlock,
  RichTextDoc,
  RichTextInline,
  RichTextMark
} from '../schema/richText'

/**
 * Renders the whitelisted rich-text subset.
 *
 * There is no `dangerouslySetInnerHTML` anywhere in this file, and that is the
 * point: the document stores structure, not markup, so nothing a user typed can
 * become an element. The schema already refused unknown nodes and unsafe link
 * schemes; this walks what survived.
 */

function applyMarks(node: ReactNode, marks: RichTextMark[] | undefined) {
  if (!marks?.length) return node

  return marks.reduce<ReactNode>((wrapped, mark) => {
    if (mark.type === 'bold') return <strong>{wrapped}</strong>
    if (mark.type === 'italic') return <em>{wrapped}</em>
    return (
      <a
        href={mark.attrs.href}
        title={mark.attrs.title}
        /*
          Set here, never by the document. An external link that could ask for
          its own `rel` is a link that can hand `window.opener` to another site
          and leak the referrer.
        */
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {wrapped}
      </a>
    )
  }, node)
}

function Inline({ nodes }: { nodes: RichTextInline[] | undefined }) {
  return (
    <>
      {(nodes ?? []).map((node, index) => (
        // Rich-text nodes have no ids and no identity beyond their position, so
        // the index is the only key available. They are never reordered.
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {node.type === 'hardBreak' ? <br /> : applyMarks(node.text, node.marks)}
        </Fragment>
      ))}
    </>
  )
}

function Block({ block }: { block: RichTextBlock }) {
  if (block.type === 'paragraph') {
    return (
      <p>
        <Inline nodes={block.content} />
      </p>
    )
  }

  const List = block.type === 'bulletList' ? 'ul' : 'ol'
  return (
    <List>
      {block.content.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index}>
          {item.content.map((paragraph, paragraphIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={paragraphIndex}>
              <Inline nodes={paragraph.content} />
            </p>
          ))}
        </li>
      ))}
    </List>
  )
}

function RichText({ doc }: { doc: RichTextDoc | undefined }) {
  if (!doc) return null
  return (
    <>
      {doc.content.map((block, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Block key={index} block={block} />
      ))}
    </>
  )
}

export default RichText
